import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, useAction, useProject } from '../../api/hooks';
import { useIsAdmin, useMe } from '../../api/session';
import type { UserSummary } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge, StateBadge } from '../../ui/Badge';
import { Button, LinkButton } from '../../ui/Button';
import { Alert, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Checkbox, Field, FormSection, Input, ListEditor, Select, Textarea } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { ConfirmDialog } from '../../ui/Modal';
import { ActorName, Card, KeyValue, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import type { Binding, ReleaseDetail } from './types';

/** WF-16: a separate, human-gated restoring operation linked to the original release. */
export function NewRollbackPage() {
  const { key, releaseId } = useParams();
  const project = useProject(key);
  const admin = useIsAdmin();
  const me = useMe();
  const navigate = useNavigate();
  usePageTitle(`Release ${releaseId} · Prepare rollback`);
  useCrumbs([
    { label: project.data?.name ?? key ?? '', to: `/projects/${key}` },
    { label: `Release ${releaseId}`, to: `/projects/${key}/releases/${releaseId}` },
    { label: 'Prepare rollback' },
  ]);
  const release = useQuery({
    queryKey: ['release', key, releaseId],
    queryFn: () => api.get<ReleaseDetail>(`/projects/${key}/releases/${releaseId}`),
  });
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/admin/users'),
    enabled: admin,
  });
  const [title, setTitle] = useState('');
  const [observation, setObservation] = useState('');
  const [revision, setRevision] = useState('');
  const [digest, setDigest] = useState('');
  const [plan, setPlan] = useState('');
  const [checks, setChecks] = useState(['']);
  const [affected, setAffected] = useState<string[]>([]);
  const [executor, setExecutor] = useState(me.user.id);
  const create = useAction(
    () =>
      api.post<{ rollbackId: string }>(`/projects/${key}/releases/${releaseId}/rollbacks`, {
        title,
        environmentStateObservation: observation,
        rollbackRevision: revision,
        artifactDigest: digest || null,
        verificationChecks: checks.filter((c) => c.trim()),
        plan,
        affectedTicketKeys: affected,
        executor: { kind: 'human', userId: executor },
      }),
    {
      success: (r) => `Prepared ${r.rollbackId}`,
      invalidate: [['release', key, releaseId]],
      onSuccess: (r) => navigate(`/projects/${key}/releases/${releaseId}/rollbacks/${r.rollbackId}`),
    },
  );
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page narrow">
      <PageHeader
        title="Prepare rollback"
        subtitle="The original release and its history stay intact. The rollback needs its own approval and attempt."
      />
      <QueryView query={release}>
        {(r) => {
          const completion = r.candidate?.binding.isCompletionTarget;
          return (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                create.mutate(undefined);
              }}
            >
              <FormSection
                title="Restoring binding"
                description={`Environment: ${r.candidate?.binding.environmentName}`}
              >
                <Field label="Title">
                  {(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} required />}
                </Field>
                <Field label="Current environment state as understood">
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={3}
                      value={observation}
                      onChange={(e) => setObservation(e.target.value)}
                      required
                    />
                  )}
                </Field>
                <div className="grid-2">
                  <Field label="Exact rollback revision">
                    {(p) => (
                      <Input
                        {...p}
                        className="mono"
                        value={revision}
                        onChange={(e) => setRevision(e.target.value)}
                        required
                      />
                    )}
                  </Field>
                  <Field label="Artifact digest" optional>
                    {(p) => (
                      <Input
                        {...p}
                        className="mono"
                        value={digest}
                        onChange={(e) => setDigest(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
                <Field label="Plan">
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={3}
                      value={plan}
                      onChange={(e) => setPlan(e.target.value)}
                      required
                    />
                  )}
                </Field>
                <div className="field">
                  <span className="label">Verification checks</span>
                  <ListEditor label="Check" items={checks} onChange={setChecks} addLabel="Add check" />
                </div>
              </FormSection>
              <FormSection
                title="Affected delivered behavior"
                description={
                  completion
                    ? 'On success, these done tickets reopen to Pending and need fresh acceptance.'
                    : 'Non-completion environment: delivered tickets are not reopened.'
                }
              >
                {r.tickets.map((t) => (
                  <Checkbox
                    key={t.key}
                    label={
                      <>
                        <span className="mono">{t.key}</span> {t.title}
                      </>
                    }
                    checked={affected.includes(t.key)}
                    onChange={(on) =>
                      setAffected(on ? [...affected, t.key] : affected.filter((k) => k !== t.key))
                    }
                  />
                ))}
                {completion && affected.length > 0 && (
                  <Alert tone="warning">Would reopen: {affected.join(', ')}</Alert>
                )}
              </FormSection>
              <FormSection title="Executor">
                <Field label="Admin">
                  {(p) => (
                    <Select {...p} value={executor} onChange={(e) => setExecutor(e.target.value)}>
                      {(users.data ?? [])
                        .filter((u) => u.role === 'admin' && u.active)
                        .map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.displayName}
                          </option>
                        ))}
                    </Select>
                  )}
                </Field>
              </FormSection>
              <ErrorAlert error={create.error} />
              <div className="form-actions">
                <LinkButton to={`/projects/${key}/releases/${releaseId}`}>Cancel</LinkButton>
                <Button type="submit" loading={create.isPending}>
                  Prepare rollback
                </Button>
              </div>
            </form>
          );
        }}
      </QueryView>
    </div>
  );
}

interface RollbackDetail {
  id: string;
  displayId: string;
  release: { id: string; displayId: string; title: string; state: string };
  project: { key: string; name: string };
  state: string;
  version: number;
  binding: Binding;
  snapshot: { affectedTickets: { key: string; title: string }[]; attemptNumber: number };
  createdAt: string;
  approvals: {
    id: string;
    displayId: string;
    state: string;
    decidedBy: { name: string } | null;
    useBy: string | null;
    consumedAt: string | null;
  }[];
  attempts: {
    id: string;
    number: number;
    state: string;
    provenance: string | null;
    registeredAt: string;
    reopened: number;
  }[];
  viewer: {
    canRequestApproval: boolean;
    canBegin: boolean;
    approvalToUse: { id: string; displayId: string; useBy: string | null } | null;
    isExecutor: boolean;
  };
}

export function RollbackPage() {
  const { key, releaseId, rollbackId } = useParams();
  const navigate = useNavigate();
  const base = `/projects/${key}/releases/${releaseId}/rollbacks/${rollbackId}`;
  const q = useQuery({ queryKey: ['rollback', base], queryFn: () => api.get<RollbackDetail>(base) });
  usePageTitle(`Release ${releaseId} · Rollback ${rollbackId}`);
  useCrumbs([
    { label: q.data?.project.name ?? key ?? '', to: `/projects/${key}` },
    { label: `Release ${releaseId}`, to: `/projects/${key}/releases/${releaseId}` },
    { label: `Rollback ${rollbackId}` },
  ]);
  const [dialog, setDialog] = useState<'request' | 'begin' | null>(null);
  const [rationale, setRationale] = useState('');
  const inv = [['rollback', base], ['release', key, releaseId], ['approvals']];
  const request = useAction(
    () => api.post(`${base}/request-approval`, { expectedVersion: q.data!.version, rationale }),
    { success: 'Rollback approval requested', invalidate: inv, onSuccess: () => setDialog(null) },
  );
  const begin = useAction(
    () =>
      api.post<{ attempt: string }>(`${base}/begin`, {
        expectedVersion: q.data!.version,
        approvalRequestId: q.data!.viewer.approvalToUse!.id,
      }),
    {
      success: (r) => `${r.attempt} registered`,
      invalidate: inv,
      onSuccess: (r) => navigate(`${base}/attempts/${r.attempt}`),
    },
  );
  return (
    <div className="page">
      <QueryView query={q}>
        {(rb) => (
          <>
            <PageHeader
              title={`Rollback ${rb.displayId}`}
              subtitle={
                <>
                  For <Link to={`/projects/${key}/releases/${releaseId}`}>{rb.release.displayId}</Link> ·{' '}
                  {rb.binding.title} → {rb.binding.environmentName}
                </>
              }
              actions={
                <>
                  <StateBadge state={rb.state} />
                  {rb.viewer.canRequestApproval && (
                    <Button size="sm" onClick={() => setDialog('request')}>
                      Request approval
                    </Button>
                  )}
                  {rb.viewer.canBegin && (
                    <Button size="sm" onClick={() => setDialog('begin')}>
                      Start approved rollback
                    </Button>
                  )}
                </>
              }
            />
            {rb.state === 'awaiting_approval' && rb.approvals[0] && (
              <Alert tone="info">
                Waiting for admin review:{' '}
                <Link to={`/approvals/${rb.approvals[0].displayId}`}>{rb.approvals[0].displayId}</Link>
              </Alert>
            )}
            <div className="split">
              <div className="stack">
                <Card title="Restoring binding">
                  <KeyValue
                    items={[
                      [
                        'Environment',
                        <span key="e">
                          {rb.binding.environmentName}{' '}
                          {rb.binding.isCompletionTarget && (
                            <Badge tone="success" dot={false}>
                              Completion target
                            </Badge>
                          )}
                        </span>,
                      ],
                      [
                        'Restore to',
                        <span key="r" className="mono">
                          {rb.binding.rollbackRevision}
                        </span>,
                      ],
                      ['Artifact', rb.binding.artifactDigest ?? '—'],
                      ['Executor', rb.binding.executor?.name ?? '—'],
                    ]}
                  />
                  <span className="small strong">Environment state as understood</span>
                  <Markdown>{rb.binding.environmentStateObservation ?? ''}</Markdown>
                  <span className="small strong">Plan</span>
                  <Markdown>{rb.binding.plan ?? ''}</Markdown>
                  <span className="small strong">Verification checks</span>
                  <ul className="checklist">
                    {rb.binding.verificationChecks.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                </Card>
                <Card
                  title="Affected tickets"
                  subtitle={
                    rb.binding.isCompletionTarget
                      ? 'Reopen to Pending on success.'
                      : 'Not reopened (non-completion environment).'
                  }
                >
                  <ul className="checklist">
                    {rb.snapshot.affectedTickets.length ? (
                      rb.snapshot.affectedTickets.map((t) => (
                        <li key={t.key}>
                          <span className="mono">{t.key}</span> {t.title}
                        </li>
                      ))
                    ) : (
                      <li className="muted">None</li>
                    )}
                  </ul>
                </Card>
              </div>
              <div className="stack">
                <Card title="Approvals" tight>
                  {rb.approvals.length === 0 ? (
                    <span className="small muted">None yet.</span>
                  ) : (
                    <ul className="checklist">
                      {rb.approvals.map((a) => (
                        <li key={a.id}>
                          <Link to={`/approvals/${a.displayId}`} className="mono">
                            {a.displayId}
                          </Link>{' '}
                          <StateBadge
                            state={a.consumedAt ? 'released' : a.state}
                            label={a.consumedAt ? 'Used' : undefined}
                          />
                          {a.decidedBy && (
                            <span className="small muted">
                              {' '}
                              by <ActorName actor={{ kind: 'human', name: a.decidedBy.name }} />
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
                <Card title="Attempts" tight>
                  {rb.attempts.length === 0 ? (
                    <span className="small muted">None yet.</span>
                  ) : (
                    <ul className="checklist">
                      {rb.attempts.map((a) => (
                        <li key={a.id}>
                          <Link to={`${base}/attempts/ATT-${a.number}`} className="mono">
                            ATT-{a.number}
                          </Link>{' '}
                          <StateBadge state={a.state} />{' '}
                          <span className="small muted">
                            <Timestamp value={a.registeredAt} />
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            </div>
            {dialog === 'request' && (
              <ConfirmDialog
                title="Request rollback approval?"
                confirmLabel="Request approval"
                onClose={() => setDialog(null)}
                onConfirm={() => request.mutate(undefined)}
                pending={request.isPending}
                error={request.error}
              >
                <Field label="Rationale" optional>
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={3}
                      value={rationale}
                      onChange={(e) => setRationale(e.target.value)}
                    />
                  )}
                </Field>
              </ConfirmDialog>
            )}
            {dialog === 'begin' && (
              <ConfirmDialog
                title={`Start rollback ATT-${rb.snapshot.attemptNumber}?`}
                confirmLabel="Register attempt and consume approval"
                onClose={() => setDialog(null)}
                onConfirm={() => begin.mutate(undefined)}
                pending={begin.isPending}
                error={begin.error}
              >
                <p>
                  The application records the attempt; you perform the rollback externally and report the
                  result.
                </p>
              </ConfirmDialog>
            )}
          </>
        )}
      </QueryView>
    </div>
  );
}
